import { Switch, Flex, UseDisclosureReturn } from '@chakra-ui/react';
import React, { useCallback, useMemo } from 'react';

import { MultiSelect } from '~/packages/common-frontend/components/MultiSelect';
import { Typography } from '~/packages/common-frontend/components/Typography';

// Exports
type ExternalOrgSelectorProps = {
  'data-testid'?: string;
  disclosure: Partial<UseDisclosureReturn>;
  externalOrgOptions: { value: string; label: string }[];
  selectedIds: string[];
  onChange: (nextSelectedIds: string[]) => void;
};
export const ExternalOrgSelector = ({
  'data-testid': testId = 'ExternalOrgSelector',
  disclosure,
  externalOrgOptions,
  selectedIds,
  onChange,
}: ExternalOrgSelectorProps) => {
  const titleText = useMemo(() => {
    if (selectedIds.length === 0) return 'Select External Parties';
    return externalOrgOptions
      .filter((option) => selectedIds.includes(option.value))
      .map((option) => option.label)
      .join(', ');
  }, [externalOrgOptions, selectedIds]);

  const handleChange = useCallback(
    (selectedId: string) => {
      const filteredIds = selectedIds.filter((id) => id !== selectedId);
      // If we removed an ID, then this is a "deselect" action, so we return the filtered IDs
      if (filteredIds.length !== selectedIds.length) {
        return onChange(filteredIds);
      }
      // If not, then this is a "select" action, so we push the new ID
      return onChange([...filteredIds, selectedId]);
    },
    [onChange, selectedIds],
  );

  return (
    <Flex
      data-testid={testId}
      gap="0.75rem"
      alignItems="center"
      flexShrink={0}
      flexWrap="wrap"
      direction={['column', 'row']}
    >
      <Flex gap="0.75rem" alignItems="center">
        <Switch
          data-testid={`${testId}.Switch`}
          size="sm"
          isChecked={disclosure.isOpen}
          onChange={disclosure.onToggle}
        />
        <Typography inline variant="detail-muted" TextProps={{ flexShrink: 0 }}>
          Make External
        </Typography>
      </Flex>

      {disclosure.isOpen && (
        <MultiSelect
          data-testid={`${testId}.MultiSelect`}
          width="13rem"
          size="sm"
          options={externalOrgOptions}
          onChange={handleChange}
          optionsHeader="Select External Parties"
          selectedOptions={selectedIds}
          titleText={titleText}
          buttonProps={{ maxWidth: '13rem' }}
        />
      )}
    </Flex>
  );
};
